import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Badge, Box, Button, ButtonDropdown, Container, FormField, Header, Link, Modal, Select, SpaceBetween,
    Spinner, StatusIndicator, Textarea,
} from '@amzn/awsui-components-react';
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import { TPEReactTable } from 'src/components/shared/TPEReactTable';
import ServiceCollection from 'src/services/ServiceCollection';
import { CommonBalancePoolColumn, CommonBalancePoolValidationResult } from 'src/models/multidimensional-allocation/CommonBalancePool';
import { TableType } from 'src/models/segment-cdt/SegmentCdtTypes';
import { WorksheetStatus } from 'src/models/multidimensional-allocation/MultiAllocationSheetData';
import { STATUS_INDICATOR_CONFIG } from 'src/components/multidimensional-allocation/common/Constants';
import { getPermissions } from 'src/components/AppPermissions';
import { GlobalAppContext } from 'src/components/App';
import { AppModules } from 'src/models/permissions/RolePermissions';
import CONSTANTS, { IS_MEC_RUN_ALWAYS_ENABLED } from 'src/utils/constants';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import {
    ACTIONS,
    commonBalancePoolBuilderReducer,
} from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderReducer';
import {
    getInitialState,
    initialState,
} from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderState';
import { CommonBalancePoolBuilderContext } from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderContext';
import { InputRow, SOURCE_TYPE_LABEL, sourceKeyOf, toInputRows } from './cbpInputs';
import AddCbpInputModal from './AddCbpInputModal';
import CbpCalculatedColumnsSection from './CbpCalculatedColumnsSection';
import CbpResultsSection from './CbpResultsSection';

/**
 * Accounting periods for Execute, newest first. Anchored to the newest *closed* period so Execute never
 * defaults to books that haven't closed: prod/beta close the previous period during the current calendar
 * month, so the window ends last month there, whereas dev/alpha/preprod (IS_MEC_RUN_ALWAYS_ENABLED) may
 * run the current month. Mirrors the same guard in the calculation/worksheet status bars.
 *
 * The option LABEL is the MDA "MMM-YYYY" display convention (e.g. "APR-2026"); the option VALUE is the
 * canonical `yyyyMM` period id (e.g. "202604") the CBP execute/validate/preview endpoints expect —
 * display and wire formats are kept decoupled per the package's PERIOD_DISPLAY_FORMAT /
 * PERIOD_VALUE_FORMAT split, so we never send the human-readable label to the backend as the period.
 */
function generateAccountingPeriods(count = 12): SelectProps.Option[] {
    const start = (IS_MEC_RUN_ALWAYS_ENABLED ? moment() : moment().subtract(1, 'months')).date(1);
    return Array.from({ length: count }, (_, i) => {
        const month = start.clone().subtract(i, 'months');
        return {
            label: month.format(CONSTANTS.PERIOD_DISPLAY_FORMAT).toUpperCase(),
            value: month.format(CONSTANTS.PERIOD_VALUE_FORMAT),
        };
    });
}

/**
 * A rejected update comes back with `validationResult.passed === false`; turn its BLOCKING findings
 * into a user-facing message so a refused save is surfaced as an error rather than shown as success.
 */
function blockingFindingsMessage(validationResult?: CommonBalancePoolValidationResult): string | undefined {
    if (!validationResult || validationResult.passed !== false) return undefined;
    const detail = (validationResult.findings ?? [])
        .filter((f) => f.severity === 'BLOCKING')
        .map((f) => f.message || f.code)
        .filter(Boolean)
        .join('; ');
    return detail || 'The pool was rejected by validation.';
}

function getStatusIndicator(status: string) {
    // Fall back gracefully if the backend returns a status not in the config, rather than throwing
    // on the array destructure and crashing the render.
    const [label, statusType, color] = STATUS_INDICATOR_CONFIG[status as WorksheetStatus]
        ?? ([status, 'info', undefined] as unknown as (typeof STATUS_INDICATOR_CONFIG)[WorksheetStatus]);
    return (
        <StatusIndicator type={statusType} colorOverride={color}>
            {label}
        </StatusIndicator>
    );
}

/** Memoised provider (cf. CalculationBuilderView) — value identity keyed on state/dispatch, not services. */
export const CommonBalancePoolBuilderProvider = (props: any) => {
    const { state, dispatch, services, poolId, canEdit, persistColumns, children } = props;
    const value = useMemo(
        () => ({ cbpBuilderState: state, cbpBuilderDispatch: dispatch, services, poolId, canEdit, persistColumns }),
        [state, dispatch, poolId, canEdit, persistColumns],
    );
    return <CommonBalancePoolBuilderContext.Provider value={value}>{children}</CommonBalancePoolBuilderContext.Provider>;
};

/**
 * Common Balance Pool builder — a route-mounted page (`/calculations/multidimensionalAllocation/
 * commonBalancePool/:poolId`), wrapped in ATPViewRestrictor. Follows the package's builder convention:
 * feature state lives in a reducer (`commonBalancePoolBuilderReducer`) exposed via
 * `CommonBalancePoolBuilderContext`; the inputs modal and Step-2 section consume that context rather
 * than receiving prop-threaded state.
 */
export default function CommonBalancePoolBuilder({ services }: { services: ServiceCollection }) {
    const { poolId } = useParams<{ poolId: string }>();
    const history = useHistory();
    const { canEdit } = getPermissions(AppModules.MULTIDIMENSIONAL_ALLOCATION);
    // Logged-in alias, for segregation of duties on approve/reject (mirrors TPAllocationActionBar).
    const { userProfile } = useContext(GlobalAppContext) ?? {};
    const [state, dispatch] = useReducerWithLogger(commonBalancePoolBuilderReducer, initialState, getInitialState);
    const { pool, isLoading, error, poolNeedsRefresh, saving, saveError, step2Dirty, period, actionBusy, actionError, actionNotice, segmentTables } = state;
    // Reject-comment modal (transient UI state — a prompt, not part of the reducer's domain state).
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectComment, setRejectComment] = useState('');

    // Accounting periods are generated client-side (MMM-YYYY, newest first); default to the current month.
    const periodOptions = useMemo(() => generateAccountingPeriods(), []);
    // Current pool for the persist merge-fallback, without making persistColumns unstable.
    const poolRef = useRef(pool);
    poolRef.current = pool;

    // Fetch the segment-CDT list once and share it through the reducer/context: the Add-input modal
    // reads the same list for its picker (no duplicate fetch), and the input source-name map derives
    // from it. The backend stores only the CDT/MDA id on `sourceRef` (no name), so on reload the Step-1
    // grid would otherwise show a raw UUID. (MDA_OUTPUT keeps any name captured at add-time.)
    const segTablesRequest = useMemo(() => ({ tableType: TableType.SEGMENT_LEVEL }), []);
    const [segTablesResp, segTablesLoading, segTablesError] = services.segmentDataTablesService.listSegmentDataTables(segTablesRequest);
    // Mirror the fetch state into the reducer, but only when it actually changes — guard on a content
    // signature (not object identity) so a new tuple reference on each render never triggers a
    // dispatch→re-render loop, and redundant dispatches are skipped.
    const segTablesSigRef = useRef<string>('');
    useEffect(() => {
        const sig = `${!!segTablesLoading}|${segTablesError ?? ''}|${(segTablesResp?.tables ?? []).map((t) => t.tableId).join(',')}`;
        if (sig === segTablesSigRef.current) return;
        segTablesSigRef.current = sig;
        dispatch(ACTIONS.SET_SEGMENT_TABLES.withPayload({ tables: segTablesResp?.tables, loading: segTablesLoading, error: segTablesError }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [segTablesResp, segTablesLoading, segTablesError]);
    const cdtNameById = useMemo(
        () => new Map((segmentTables ?? []).map((t) => [t.tableId, t.tableName])),
        [segmentTables],
    );
    const resolveSourceName = useCallback(
        (col: CommonBalancePoolColumn) => (col.sourceRef?.cdtTableId ? cdtNameById.get(col.sourceRef.cdtTableId) : undefined),
        [cdtNameById],
    );

    // Fetch the pool whenever a refresh is requested (mount + explicit refresh).
    useEffect(() => {
        if (!poolNeedsRefresh) return undefined;
        let active = true;
        dispatch(ACTIONS.SET_LOADING.withPayload(true));
        services.commonBalancePoolService
            .getCommonBalancePool(poolId)
            .then((result) => { if (active) dispatch(ACTIONS.SET_POOL.withPayload(result)); })
            .catch((ex) => { if (active) dispatch(ACTIONS.SET_ERROR.withPayload(ex instanceof Error ? ex.message : String(ex))); });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [poolId, poolNeedsRefresh]);

    // Default the period selector to the current month once.
    useEffect(() => {
        if (period === undefined) dispatch(ACTIONS.SET_PERIOD.withPayload(periodOptions[0] ?? null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** The single UpdateCommonBalancePool choke point (with the dangling-reference guard). */
    const persistColumns = useCallback(async (nextColumns: CommonBalancePoolColumn[]): Promise<boolean> => {
        // A CALC column must not depend on a columnId absent from the set being persisted (e.g. removing/
        // editing an input a calculated column still references). Centralised so it covers every path.
        const knownIds = new Set(nextColumns.map((c) => c.columnId));
        const dangling = Array.from(new Set(
            nextColumns
                .filter((c) => c.kind === 'CALC')
                .flatMap((c) => c.dependsOn ?? [])
                .filter((id) => !knownIds.has(id)),
        ));
        if (dangling.length > 0) {
            dispatch(ACTIONS.SET_SAVE_ERROR.withPayload(
                `Cannot save: calculated column(s) reference removed input(s): ${dangling.join(', ')}. `
                + 'Update or remove those calculated columns first.',
            ));
            return false;
        }
        dispatch(ACTIONS.SET_SAVE_ERROR.withPayload(undefined));
        dispatch(ACTIONS.SET_SAVING.withPayload(true));
        try {
            const { pool: updated, validationResult } = await services.commonBalancePoolService.updateCommonBalancePool(poolId, { columns: nextColumns });
            // A BLOCKING validation finding means the update was refused — surface it instead of
            // reporting success on a save that never applied.
            const blocked = blockingFindingsMessage(validationResult);
            if (blocked) {
                dispatch(ACTIONS.SET_SAVE_ERROR.withPayload(blocked));
                return false;
            }
            // Trust the server's echoed columns (fall back to what we sent), matching runStatusAction —
            // both write paths take the persisted columns from the response rather than disagreeing.
            dispatch(ACTIONS.SET_POOL.withPayload({ ...(updated ?? poolRef.current), columns: updated?.columns ?? nextColumns }));
            return true;
        } catch (ex) {
            dispatch(ACTIONS.SET_SAVE_ERROR.withPayload(ex instanceof Error ? ex.message : String(ex)));
            return false;
        } finally {
            dispatch(ACTIONS.SET_SAVING.withPayload(false));
        }
    }, [poolId, services, dispatch]);

    const handleEditInput = (row: InputRow) => dispatch(ACTIONS.OPEN_INPUT_MODAL.withPayload(row));
    const startAdd = () => dispatch(ACTIONS.OPEN_INPUT_MODAL.withPayload(undefined));

    const handleRemoveInput = (row: InputRow) => {
        const nextColumns = (poolRef.current?.columns ?? []).filter(
            (col) => !(col.kind === 'INPUT' && sourceKeyOf(col) === row.sourceKey),
        );
        persistColumns(nextColumns);
    };

    /**
     * Persist the definition, driving a status transition: Save draft / Submit for review, and the
     * reviewer's Approve (-> ACTIVE) / Reject (-> DRAFT_IN_PROGRESS). `comment` is carried on the wire
     * (required on a rejection so the author knows what to change). The backend enforces
     * segregation-of-duties (submitter != approver), so the UI need not.
     */
    const runStatusAction = async (
        busyKind: 'save' | 'submit' | 'approve' | 'reject',
        status: WorksheetStatus,
        successMessage: string,
        comment?: string,
    ): Promise<boolean> => {
        dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(undefined));
        dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(undefined));
        dispatch(ACTIONS.SET_ACTION_BUSY.withPayload(busyKind));
        try {
            // Save draft / Submit carry the draft's columns; Approve / Reject are status-only. Sending
            // columns on Approve trips the backend's "cannot be approved and edited in the same request"
            // guard (touchesContent = columns != null), and the backend preserves stored columns when the
            // request omits them.
            const includeColumns = busyKind === 'save' || busyKind === 'submit';
            const { pool: updated, validationResult } = await services.commonBalancePoolService.updateCommonBalancePool(poolId, {
                status,
                ...(includeColumns ? { columns: poolRef.current?.columns ?? [] } : {}),
                ...(comment ? { comment } : {}),
            });
            const blocked = blockingFindingsMessage(validationResult);
            if (blocked) {
                dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(blocked));
                return false;
            }
            dispatch(ACTIONS.SET_POOL.withPayload({
                ...(updated ?? poolRef.current),
                columns: updated?.columns ?? poolRef.current?.columns,
                status: updated?.status ?? status,
            }));
            dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(successMessage));
            return true;
        } catch (ex) {
            dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(ex instanceof Error ? ex.message : String(ex)));
            return false;
        } finally {
            dispatch(ACTIONS.SET_ACTION_BUSY.withPayload(''));
        }
    };

    const handleSaveDraft = () => runStatusAction('save', 'DRAFT_IN_PROGRESS', 'Draft saved.');
    const handleSubmitForReview = () => runStatusAction('submit', 'PENDING_APPROVAL', 'Submitted for review.');
    const handleApprove = () => runStatusAction('approve', 'ACTIVE', 'Pool approved.');
    // Open/close both clear any prior actionError so the reject modal (and the page-level Alert) never
    // shows a stale error from an abandoned attempt — cancelling or reopening starts clean.
    const openReject = () => {
        dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(undefined));
        setRejectOpen(true);
    };
    const closeReject = () => {
        setRejectOpen(false);
        setRejectComment('');
        dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(undefined));
    };
    // Reject requires a reason (model contract); the modal's button is disabled until one is entered.
    // Close/clear only on success — a failed reject keeps the modal open with the typed reason intact.
    const submitReject = async () => {
        const comment = rejectComment.trim();
        if (!comment) return;
        const ok = await runStatusAction('reject', 'DRAFT_IN_PROGRESS', 'Pool rejected — returned to draft.', comment);
        if (ok) closeReject();
    };

    const handleExecute = async () => {
        if (!period?.value) {
            dispatch(ACTIONS.SET_ACTION_ERROR.withPayload('Select a period to execute.'));
            return;
        }
        dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(undefined));
        dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(undefined));
        dispatch(ACTIONS.SET_ACTION_BUSY.withPayload('execute'));
        try {
            const execution = await services.commonBalancePoolService.executeCommonBalancePool(poolId, period.value);
            // Send the canonical yyyyMM id (period.value); surface the readable MMM-YYYY label to the user.
            // A terminal FAILED/PARTIAL/TIMED_OUT must not render as a green success — route those to the
            // error banner; only SUCCESS/PENDING/RUNNING are non-error. (The 202 carries only
            // { executionId, state }; the full outcome — errorMessage / validationResult — comes from
            // polling GetCommonBalancePoolExecutionStatus, tracked as a follow-up once that path is live.)
            const periodLabel = period.label ?? period.value;
            const state = execution?.state;
            const message = state ? `Execution ${state.toLowerCase()} for ${periodLabel}.` : `Execution started for ${periodLabel}.`;
            if (state === 'FAILED' || state === 'PARTIAL' || state === 'TIMED_OUT') {
                dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(message));
            } else {
                dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(message));
            }
        } catch (ex) {
            dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(ex instanceof Error ? ex.message : String(ex)));
        } finally {
            dispatch(ACTIONS.SET_ACTION_BUSY.withPayload(''));
        }
    };

    const backLink = (
        <Link onFollow={() => history.push(CONSTANTS.PAGE_NAV.MULTIDIMENSIONAL_ALLOCATION.URL)}>
            &#8592; Common Balance Pools
        </Link>
    );

    if (isLoading) {
        return (
            <div className="multidimensionalAllocationBuilder">
                <SpaceBetween direction="vertical" size="l">
                    {backLink}
                    <Box textAlign="center" padding="l">
                        <Spinner size="large" />
                        <Box variant="p">Loading common balance pool…</Box>
                    </Box>
                </SpaceBetween>
            </div>
        );
    }
    if (error) {
        return (
            <div className="multidimensionalAllocationBuilder">
                <SpaceBetween direction="vertical" size="l">
                    {backLink}
                    <Alert type="error" header="Error loading common balance pool">{error}</Alert>
                </SpaceBetween>
            </div>
        );
    }
    if (!pool) {
        return (
            <div className="multidimensionalAllocationBuilder">
                <SpaceBetween direction="vertical" size="l">
                    {backLink}
                    <Alert type="warning" header="Pool not found">No common balance pool found with id: {poolId}</Alert>
                </SpaceBetween>
            </div>
        );
    }

    const inputRows = toInputRows(pool.columns, resolveSourceName);
    // Single busy flag so an in-flight input persist (`saving`) and an in-flight lifecycle action
    // (`actionBusy`) each disable the other's buttons — avoids an overlapping-write / lost update.
    const busy = saving || actionBusy !== '';
    // Save draft / Submit for review are draft-only — a non-draft status disables both. Derived once
    // so the two guards (and their disabledReason strings) can't drift apart.
    const notDraft = !!pool.status && pool.status !== 'DRAFT_IN_PROGRESS';
    // Segregation of duties: the submitter must not approve/reject their own pool. The backend enforces
    // this on the wire; the UI mirrors it (like TPAllocationActionBar) so the block is visible up front.
    const isSubmitter = !!pool.submitUser && !!userProfile?.user && userProfile.user === pool.submitUser;
    const selfReviewDisabledReason = isSubmitter
        ? 'You submitted this pool for review; a different reviewer must approve or reject it.'
        : undefined;
    const inputColumnDefinitions = [
        {
            accessor: 'num',
            Header: () => <div className="cell-text-non-sortable">#</div>,
            Cell: ({ value }: any) => <div className="cell-text">{value}</div>,
        },
        {
            accessor: 'sourceType',
            Header: () => <div className="cell-text-non-sortable">Input type</div>,
            Cell: ({ row }: any) => (
                <div className="cell-text">
                    {row.original.sourceType ? SOURCE_TYPE_LABEL[row.original.sourceType] ?? row.original.sourceType : '-'}
                </div>
            ),
        },
        {
            accessor: 'sourceObject',
            Header: () => <div className="cell-text-non-sortable">Source object</div>,
            Cell: ({ value }: any) => <div className="cell-text">{value}</div>,
        },
        {
            accessor: 'columns',
            Header: () => <div className="cell-text-non-sortable">Columns</div>,
            Cell: ({ row }: any) => (
                <SpaceBetween direction="horizontal" size="xs">
                    {(row.original.columns as string[]).map((c) => (
                        <Badge key={c}>{c}</Badge>
                    ))}
                </SpaceBetween>
            ),
        },
        {
            accessor: 'grain',
            Header: () => <div className="cell-text-non-sortable">Grain</div>,
            Cell: () => <div className="cell-text">{pool.grain}</div>,
        },
        {
            accessor: 'actions',
            Header: () => <div className="cell-text-non-sortable">Action</div>,
            Cell: (e: any) => (
                <div className="rowActions">
                    <ButtonDropdown
                        className="rowContextMenu gridContextMenu"
                        ariaLabel={`Actions for ${(e.row.original as InputRow).sourceObject}`}
                        disabled={!canEdit || busy || step2Dirty}
                        expandToViewport
                        items={[
                            { id: 'edit', text: 'Edit' },
                            { id: 'remove', text: 'Remove' },
                        ]}
                        onItemClick={({ detail }) => {
                            const item = e.row.original as InputRow;
                            if (detail.id === 'edit') handleEditInput(item);
                            else if (detail.id === 'remove') handleRemoveInput(item);
                        }}
                    />
                </div>
            ),
        },
    ];

    return (
        <CommonBalancePoolBuilderProvider
            state={state}
            dispatch={dispatch}
            services={services}
            poolId={poolId}
            canEdit={canEdit}
            persistColumns={persistColumns}
        >
            <div className="multidimensionalAllocationBuilder">
                <SpaceBetween direction="vertical" size="l">
                    {backLink}
                    <Header variant="h1" description={pool.poolId}>{pool.poolName ?? pool.poolId}</Header>

                    {/* Status + Period on the left, lifecycle actions on the right (matches the worksheet builder). */}
                    <Box>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <SpaceBetween direction="horizontal" size="s" alignItems="center">
                                <Box variant="awsui-key-label" padding={{ top: 'xxs' }}>
                                    Pool Status: {pool.status ? getStatusIndicator(pool.status) : '-'}
                                </Box>
                                <Box variant="awsui-key-label" padding={{ top: 'xxs' }}>Period</Box>
                                <Select
                                    selectedOption={period ?? null}
                                    onChange={({ detail }) => dispatch(ACTIONS.SET_PERIOD.withPayload(detail.selectedOption))}
                                    options={periodOptions}
                                    placeholder="Select period"
                                    disabled={periodOptions.length === 0}
                                    filteringType="auto"
                                />
                            </SpaceBetween>

                            <SpaceBetween direction="horizontal" size="s">
                                {canEdit && pool.status === 'PENDING_APPROVAL' && (
                                    <>
                                        <Button
                                            onClick={openReject}
                                            loading={actionBusy === 'reject'}
                                            disabled={busy || isSubmitter}
                                            disabledReason={selfReviewDisabledReason}
                                        >
                                            Reject
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleApprove}
                                            loading={actionBusy === 'approve'}
                                            disabled={busy || isSubmitter}
                                            disabledReason={selfReviewDisabledReason}
                                        >
                                            Approve
                                        </Button>
                                    </>
                                )}
                                <Button
                                    onClick={handleSaveDraft}
                                    loading={actionBusy === 'save'}
                                    disabled={!canEdit || busy || step2Dirty || notDraft}
                                    disabledReason={
                                        !canEdit ? 'You do not have edit permissions.'
                                            : step2Dirty ? 'Save your calculated-column changes (Step 2) first.'
                                                : notDraft ? 'Save draft is only available while the pool is a draft.'
                                                    : undefined
                                    }
                                >
                                    Save draft
                                </Button>
                                <Button
                                    onClick={handleSubmitForReview}
                                    loading={actionBusy === 'submit'}
                                    disabled={!canEdit || busy || inputRows.length === 0 || step2Dirty || notDraft}
                                    disabledReason={
                                        !canEdit ? 'You do not have edit permissions.'
                                            : step2Dirty ? 'Save your calculated-column changes (Step 2) first.'
                                                : notDraft ? 'Submit for review is only available while the pool is a draft.'
                                                    : inputRows.length === 0 ? 'Add at least one input before submitting.'
                                                        : undefined
                                    }
                                >
                                    Submit for review
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleExecute}
                                    loading={actionBusy === 'execute'}
                                    disabled={!canEdit || busy || inputRows.length === 0 || !period || step2Dirty}
                                    disabledReason={
                                        !canEdit ? 'You do not have edit permissions.'
                                            : step2Dirty ? 'Save your calculated-column changes (Step 2) first.'
                                                : inputRows.length === 0 ? 'Add at least one input before executing.'
                                                    : !period ? 'Select a period to execute.'
                                                        : undefined
                                    }
                                >
                                    Execute
                                </Button>
                            </SpaceBetween>
                        </div>
                    </Box>

                    {actionError && (
                        <Alert type="error" dismissible header="Action failed" onDismiss={() => dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(undefined))}>
                            {actionError}
                        </Alert>
                    )}
                    {actionNotice && (
                        <Alert type="success" dismissible onDismiss={() => dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(undefined))}>
                            {actionNotice}
                        </Alert>
                    )}
                    {/* A rejection returns the pool to draft with the reviewer's reason; surface it so the
                        author sees what to change (otherwise the mandatory reject reason is write-only). */}
                    {pool.status === 'DRAFT_IN_PROGRESS' && pool.comment && (
                        <Alert type="info" header="Returned for changes">{pool.comment}</Alert>
                    )}

                    {/* Step 1: Configure inputs */}
                    <div className="stepsWizard">
                        <Container
                            data-class="polarisExpandableSection"
                            header={<Header variant="h3">Step 1: Configure inputs</Header>}
                            footer={
                                <div className="actionButtonsContainer">
                                    <Box float="left">
                                        <Button
                                            variant="normal"
                                            onClick={startAdd}
                                            disabled={!canEdit || busy || step2Dirty}
                                            disabledReason={
                                                !canEdit ? 'You do not have edit permissions.'
                                                    : step2Dirty ? 'Save your calculated-column changes (Step 2) first.'
                                                        : undefined
                                            }
                                        >
                                            Add input
                                        </Button>
                                    </Box>
                                </div>
                            }
                        >
                            <SpaceBetween direction="vertical" size="m">
                                <Box color="text-body-secondary">
                                    Bring inputs to the {pool.grain} grain. Each input contributes one or more columns;
                                    segment CDTs and allocation outputs must match the pool grain to be selectable.
                                </Box>
                                {saveError && (
                                    <Alert type="error" dismissible header="Update failed" onDismiss={() => dispatch(ACTIONS.SET_SAVE_ERROR.withPayload(undefined))}>
                                        {saveError}
                                    </Alert>
                                )}
                                <TPEReactTable {...{
                                    data: inputRows,
                                    columnDefinitions: inputColumnDefinitions,
                                    className: 'cbpInputsGrid',
                                    sortable: false,
                                    key: 'cbpInputsGrid',
                                    emptyMessage: 'No inputs configured yet.',
                                }} />
                            </SpaceBetween>
                        </Container>
                    </div>

                    {/* Step 2: Configure calculated columns — consumes the builder context. */}
                    <div className="stepsWizard">
                        <Container
                            data-class="polarisExpandableSection"
                            header={<Header variant="h3">Step 2: Configure calculated columns</Header>}
                        >
                            <CbpCalculatedColumnsSection />
                        </Container>
                    </div>

                    {/* Step 3: Validate & preview — a bounded materialized preview for the selected period.
                        The Validate / Download actions and the sum-vs-GL logic are wired separately. */}
                    <div className="stepsWizard">
                        <Container
                            data-class="polarisExpandableSection"
                            header={<Header variant="h3">Step 3: Validate &amp; preview</Header>}
                        >
                            <CbpResultsSection />
                        </Container>
                    </div>
                </SpaceBetween>

                <AddCbpInputModal />

                <Modal
                    visible={rejectOpen}
                    onDismiss={closeReject}
                    header="Reject common balance pool"
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={closeReject} disabled={actionBusy === 'reject'}>Cancel</Button>
                                <Button variant="primary" onClick={submitReject} loading={actionBusy === 'reject'} disabled={!rejectComment.trim()}>
                                    Reject
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    <SpaceBetween direction="vertical" size="m">
                        {/* A failed reject keeps the modal open; surface the error in-modal (the page-level
                            actionError Alert sits behind the modal scrim, so a backend failure would
                            otherwise look like a silent no-op and invite a blind retry). */}
                        {actionError && <Alert type="error">{actionError}</Alert>}
                        <FormField label="Reason for rejection" description="Sent back to the author so they know what to change.">
                            <Textarea
                                value={rejectComment}
                                onChange={({ detail }) => setRejectComment(detail.value)}
                                placeholder="Explain why this pool is being rejected"
                                rows={3}
                            />
                        </FormField>
                    </SpaceBetween>
                </Modal>
            </div>
        </CommonBalancePoolBuilderProvider>
    );
}
